'use client';

import { useEffect, useState } from 'react';

import {
  Container,
  Row,
  Col,
  Alert,
  Spinner,
  Badge
} from 'react-bootstrap';

import {
  getBookingsReport,
  downloadBookingsCSV
} from 'features/admin/reportApi';

import Button from 'components/base/Button';

import { FontAwesomeIcon }
  from '@fortawesome/react-fontawesome';

import {
  faCalendarAlt,
  faCheckCircle,
  faClock,
  faTimesCircle,
  faSearch,
  faSpinner,
  faLocationDot,
  faDownload,
  faTimes,
  faCheckDouble,
  faFileExport,
  faPoundSign,
  faUser,
  faEnvelope,
  faBan
} from '@fortawesome/free-solid-svg-icons';

import ConfirmBookingModal from 'components/modals/ConfirmBookingModal';
import { cancelBooking } from 'features/booking/bookingApi';

interface Booking {
  id: number;

  locum_id: number;
  locum_name: string;
  locum_email: string;

  shift_id: number;

  shift_date: string;

  start_time: string;
  end_time: string;

  pharmacy_name: string;

  location: string;

  pay_rate: string;

  locum_role: string;

  status: string;

  booked_at: string;
}

const BookingReportPage = () => {

  const [loading, setLoading] =
    useState(true);

  const [exportLoading, setExportLoading] =
    useState(false);

  const [error, setError] =
    useState('');

  const [summary, setSummary] =
    useState<any>({});

  const [bookings, setBookings] =
    useState<Booking[]>([]);

  const [startDate, setStartDate] =
    useState('');

  const [endDate, setEndDate] =
    useState('');

  const [statusFilter, setStatusFilter] =
    useState('');

  const [search, setSearch] =
    useState('');

  const [page, setPage] =
    useState(1);

  const [totalPages, setTotalPages] =
    useState(1);

  const [total, setTotal] =
    useState(0);

  const [cancelingId, setCancelingId] = useState<number | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const itemsPerPage = 10;

  const handleConfirmCancel = async () => {
    if (!selectedBooking) return;

    try {
      setCancelingId(selectedBooking.id);

      await cancelBooking(selectedBooking.id);

      // update UI instantly
      setBookings(prev =>
        prev.map(b =>
          b.id === selectedBooking.id
            ? { ...b, status: 'cancelled' }
            : b
        )
      );
      setShowCancelModal(false);
      setShowSuccessModal(true);
    } catch (err: any) {
      alert(err.message);
    } finally {
      setCancelingId(null);
    }
  };

  // FETCH REPORT
  const fetchReport = async (
    pageNumber = 1
  ) => {

    try {

      setLoading(true);

      const filters: any = {};

      if (startDate)
        filters.start_date = startDate;

      if (endDate)
        filters.end_date = endDate;

      if (statusFilter)
        filters.status = statusFilter;

      if (search)
        filters.search = search;

      const data =
        await getBookingsReport(filters);

      let bookingsData =
        data.bookings || [];

      // PAGINATION
      const totalRecords =
        bookingsData.length;

      const totalPagesCount =
        Math.ceil(
          totalRecords /
          itemsPerPage
        );

      const startIndex =
        (pageNumber - 1) *
        itemsPerPage;

      const endIndex =
        startIndex +
        itemsPerPage;

      const paginatedBookings =
        bookingsData.slice(
          startIndex,
          endIndex
        );

      setSummary(
        data.summary || {}
      );

      setBookings(
        paginatedBookings
      );

      setPage(pageNumber);

      setTotal(totalRecords);

      setTotalPages(
        totalPagesCount
      );

    } catch (err: any) {

      setError(
        err.message ||
        'Failed to fetch booking reports'
      );

    } finally {

      setLoading(false);

    }
  };

  // EXPORT CSV
  const handleExportCSV =
    async () => {

      try {

        setExportLoading(true);

        const filters: any = {};

        if (startDate)
          filters.start_date = startDate;

        if (endDate)
          filters.end_date = endDate;

        if (statusFilter)
          filters.status = statusFilter;

        if (search)
          filters.search = search;

        await downloadBookingsCSV(filters);

      } catch (err: any) {

        setError(
          err.message ||
          'Failed to export bookings'
        );

      } finally {

        setExportLoading(false);

      }
    };

  const handleCancelClick = (booking: Booking) => {
    setSelectedBooking(booking);
    setShowCancelModal(true);
  };

  // DEFAULT DATE RANGE
  useEffect(() => {

    const today =
      new Date();

    const lastWeek =
      new Date(
        today.getTime() -
        7 * 24 * 60 * 60 * 1000
      );

    const formatDate =
      (date: Date) =>
        date.toISOString().split('T')[0];

    setStartDate(
      formatDate(lastWeek)
    );

    setEndDate(
      formatDate(today)
    );

  }, []);

  useEffect(() => {

    if (
      startDate &&
      endDate
    ) {

      fetchReport(1);

    }

  }, [
    startDate,
    endDate,
    statusFilter,
    search
  ]);

  return (
    <Container
      fluid
      className='py-3'
    >

      {/* HEADER */}
      <Row className='mb-4'>

        <Col>

          <div className='d-flex flex-wrap justify-content-between align-items-start gap-3'>

            <div>

              <p className='text-body-tertiary text-uppercase fs-10 fw-bold mb-1'>

                <FontAwesomeIcon
                  icon={faFileExport}
                  className='me-2'
                />

                Admin Analytics

              </p>

              <h2 className='text-body-emphasis mb-1'>
                Booking Reports
              </h2>

              <p className='mb-0 text-body-tertiary fs-9'>
                Analyze bookings, cancellations,
                locums, and pharmacy activity.
              </p>

            </div>

          </div>

        </Col>

      </Row>

      {/* STATS */}
      <Row className='mb-4 g-3'>

        <Col md={2} lg={2.4}>

          <div className='card border-0 shadow-sm h-100'>

            <div className='card-body'>

              <div className='d-flex justify-content-between align-items-start'>

                <div>

                  <p className='text-body-tertiary fs-10 fw-bold text-uppercase mb-1'>
                    Total Bookings
                  </p>

                  <h3 className='text-body-emphasis mb-0'>
                    {summary.total_bookings || 0}
                  </h3>

                </div>

                <span className='avatar avatar-m text-center'>

                  <FontAwesomeIcon
                    icon={faCalendarAlt}
                    className='text-primary'
                  />

                </span>

              </div>

            </div>

          </div>

        </Col>

        <Col md={2} lg={2.4}>

          <div className='card border-0 shadow-sm h-100'>

            <div className='card-body'>

              <div className='d-flex justify-content-between align-items-start'>

                <div>

                  <p className='text-body-tertiary fs-10 fw-bold text-uppercase mb-1'>
                    Confirmed
                  </p>

                  <h3 className='text-success mb-0'>
                    {summary.confirmed_bookings || 0}
                  </h3>

                </div>

                <span className='avatar avatar-m text-center'>

                  <FontAwesomeIcon
                    icon={faCheckCircle}
                    className='text-success'
                  />

                </span>

              </div>

            </div>

          </div>

        </Col>

        <Col md={2} lg={2.4}>

          <div className='card border-0 shadow-sm h-100'>

            <div className='card-body'>

              <div className='d-flex justify-content-between align-items-start'>

                <div>

                  <p className='text-body-tertiary fs-10 fw-bold text-uppercase mb-1'>
                    Cancelled
                  </p>

                  <h3 className='text-danger mb-0'>
                    {summary.cancelled_bookings || 0}
                  </h3>

                </div>

                <span className='avatar avatar-m text-center'>

                  <FontAwesomeIcon
                    icon={faBan}
                    className='text-danger'
                  />

                </span>

              </div>

            </div>

          </div>

        </Col>

        <Col md={2} lg={2.4}>

          <div className='card border-0 shadow-sm h-100'>

            <div className='card-body'>

              <div className='d-flex justify-content-between align-items-start'>

                <div>

                  <p className='text-body-tertiary fs-10 fw-bold text-uppercase mb-1'>
                    Completed
                  </p>

                  <h3 className='text-info mb-0'>
                    {summary.completed_bookings || 0}
                  </h3>

                </div>

                <span className='avatar avatar-m text-center'>

                  <FontAwesomeIcon
                    icon={faCheckDouble}
                    className='text-info'
                  />

                </span>

              </div>

            </div>

          </div>

        </Col>

        <Col md={2} lg={2.4}>

          <div className='card border-0 shadow-sm h-100'>

            <div className='card-body'>

              <div className='d-flex justify-content-between align-items-start'>

                <div>

                  <p className='text-body-tertiary fs-10 fw-bold text-uppercase mb-1'>
                    Cancellation Rate
                  </p>

                  <h3 className='text-warning mb-0'>
                    {summary.cancellation_rate || 0}%
                  </h3>

                </div>

                <span className='avatar avatar-m text-center'>

                  <FontAwesomeIcon
                    icon={faTimesCircle}
                    className='text-warning'
                  />

                </span>

              </div>

            </div>

          </div>

        </Col>

      </Row>

      {/* ERROR */}
      {error && (

        <Row className='mb-4'>

          <Col>

            <Alert
              variant='danger'
              dismissible
              onClose={() => setError('')}
            >

              <FontAwesomeIcon
                icon={faTimes}
                className='me-2'
              />

              {error}

            </Alert>

          </Col>

        </Row>
      )}

      {/* FILTERS */}
      <Row className='mb-4 g-2'>
        <Col lg={3}>

          <label className='form-label'>
            Search
          </label>

          <input
            type='text'
            className='form-control'
            placeholder='Search locum or pharmacy'
            value={search}
            onChange={e =>
              setSearch(e.target.value)
            }
          />

        </Col>

        <Col lg={2}>

          <label className='form-label'>
            Start Date
          </label>

          <input
            type='date'
            className='form-control'
            value={startDate}
            onChange={e =>
              setStartDate(e.target.value)
            }
          />

        </Col>

        <Col lg={2}>

          <label className='form-label'>
            End Date
          </label>

          <input
            type='date'
            className='form-control'
            value={endDate}
            onChange={e =>
              setEndDate(e.target.value)
            }
          />

        </Col>

        <Col lg={2}>

          <label className='form-label'>
            Status
          </label>

          <select
            className='form-select'
            value={statusFilter}
            onChange={e =>
              setStatusFilter(e.target.value)
            }
          >

            <option value=''>
              All
            </option>

            <option value='confirmed'>
              Confirmed
            </option>

            <option value='completed'>
              Completed
            </option>

            <option value='cancelled'>
              Cancelled
            </option>

          </select>

        </Col>



      </Row>

      <Row className='mb-4'>

        <Col lg={2} className='d-flex gap-2'>

          <Button
            variant='phoenix-primary'
            className='flex-grow-1'
            onClick={() => fetchReport(1)}
            startIcon={
              <FontAwesomeIcon icon={faSearch} />
            }
          >
            Search
          </Button>

        </Col>

        <Col lg={2}>

          <Button
            variant='phoenix-primary'
            onClick={handleExportCSV}
            disabled={
              exportLoading ||
              bookings.length === 0
            }
            startIcon={
              exportLoading ? (
                <FontAwesomeIcon
                  icon={faSpinner}
                  spin
                />
              ) : (
                <FontAwesomeIcon
                  icon={faDownload}
                />
              )
            }
          >
            {exportLoading
              ? 'Exporting...'
              : 'Export CSV'}
          </Button>

        </Col>

      </Row>

      {/* LOADING */}
      {loading && (

        <Row className='py-5'>

          <Col className='text-center'>

            <Spinner
              animation='border'
              variant='primary'
              className='mb-3'
            />

            <p className='text-body-tertiary fw-semibold'>
              Loading booking reports...
            </p>

          </Col>

        </Row>
      )}

      {/* TABLE */}
      {!loading && (

        <Row>

          <Col>

            <div className='card border-0 shadow-sm overflow-hidden'>

              <div className='table-responsive'>

                <table className='table align-middle mb-0 fs-8' style={{ tableLayout: 'fixed' }}>

                  <thead className="bg-body-secondary">
                    <tr>
                      <th className="ps-4 py-3" style={{ width: '22%' }}>
                        Locum
                      </th>

                      <th className="py-3" style={{ width: '12%' }}>
                        Pharmacy
                      </th>

                      <th className="py-3" style={{ width: '15%' }}>
                        Shift
                      </th>

                      <th className="py-3" style={{ width: '30%' }}>
                        Location
                      </th>

                      <th className="py-3" style={{ width: '8%' }}>
                        Pay
                      </th>

                      <th className="py-3 text-center" style={{ width: '8%' }}>
                        Status
                      </th>

                      <th className="py-3 text-center" style={{ width: '9%' }}>
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody>

                    {bookings.length === 0 ? (

                      <tr>

                        <td
                          colSpan={6}
                          className='text-center py-5'
                        >

                          No bookings found

                        </td>

                      </tr>

                    ) : (

                      bookings.map(booking => (

                        <tr key={booking.id}>

                          <td className='ps-4 py-3'>

                            <div>

                              <p className='mb-1 fw-semibold'>

                                <FontAwesomeIcon
                                  icon={faUser}
                                  className='me-2 text-primary'
                                />

                                {booking.locum_name}

                              </p>

                              <small className='text-body-tertiary'>

                                <FontAwesomeIcon
                                  icon={faEnvelope}
                                  className='me-2'
                                />

                                {booking.locum_email}

                              </small>

                            </div>

                          </td>

                          <td className='py-3'>

                            <div>

                              <p className='mb-1 fw-semibold'>
                                {booking.pharmacy_name}
                              </p>

                              <small className='text-body-tertiary'>
                                {booking.locum_role}
                              </small>

                            </div>

                          </td>

                          <td className='py-3'>

                            <div>

                              <p className='mb-1 fw-semibold'>

                                <FontAwesomeIcon
                                  icon={faCalendarAlt}
                                  className='me-2 text-info'
                                />

                                {new Date(
                                  booking.shift_date
                                ).toLocaleDateString(
                                  'en-GB'
                                )}

                              </p>

                              <small className='text-body-tertiary'>

                                <FontAwesomeIcon
                                  icon={faClock}
                                  className='me-2'
                                />

                                {booking.start_time} - {booking.end_time}

                              </small>

                            </div>

                          </td>

                         <td className="py-3">

                            <div
                              className="text-wrap"
                              style={{
                                maxWidth: '320px',
                                overflowWrap: 'break-word'
                              }}
                              title={booking.location}
                            >
                              <FontAwesomeIcon
                                icon={faLocationDot}
                                className="me-2 text-danger"
                              />

                              {booking.location}
                            </div>

                          </td>

                          <td className='py-3'>

                            <span className='fw-bold'>

                              <FontAwesomeIcon
                                icon={faPoundSign}
                                className='me-2'
                              />

                              {booking.pay_rate}

                            </span>

                          </td>

                          <td className='text-center py-3'>

                            <Badge
                              bg={
                                booking.status === 'confirmed'
                                  ? 'success'
                                  : booking.status === 'completed'
                                    ? 'primary'
                                    : 'danger'
                              }
                              className='rounded-pill'
                            >

                              {booking.status}

                            </Badge>

                          </td>
                          <td className='text-center py-3 pe-3'>

                            {booking.status === 'confirmed' && (
                              <Button
                                size="sm"
                                variant="phoenix-danger"
                                onClick={() => handleCancelClick(booking)}
                                disabled={cancelingId === booking.id}
                                className="px-3"
                              >
                                {cancelingId === booking.id ? (
                                  <>
                                    <Spinner
                                      animation="border"
                                      size="sm"
                                      className="me-2"
                                    />
                                    Cancelling...
                                  </>
                                ) : (
                                  'Cancel'
                                )}
                              </Button>
                            )}

                          </td>

                        </tr>

                      ))
                    )}

                  </tbody>

                </table>

              </div>

              {/* Pagination */}
              {!loading && bookings.length > 0 && (
                <div className="card-footer bg-body border-top border-translucent py-3">
                  <ul className="pagination justify-content-center mb-0 gap-1">
                    {page > 1 && (
                      <li className="page-item">
                        <button
                          className="page-link"
                          onClick={() => fetchReport(page - 1)}
                        >
                          Previous
                        </button>
                      </li>
                    )}

                    {[...Array(totalPages)].map((_, i) => (
                      <li
                        key={i}
                        className={`page-item ${page === i + 1 ? 'active' : ''
                          }`}
                      >
                        <button
                          className="page-link"
                          onClick={() => fetchReport(i + 1)}
                        >
                          {i + 1}
                        </button>
                      </li>
                    ))}

                    {page < totalPages && (
                      <li className="page-item">
                        <button
                          className="page-link"
                          onClick={() => fetchReport(page + 1)}
                        >
                          Next
                        </button>
                      </li>
                    )}
                  </ul>
                </div>
              )}

            </div>

          </Col>

        </Row>
      )}

      <ConfirmBookingModal
        show={showCancelModal}
        title="Cancel Booking"
        message="Are you sure you want to cancel this booking?"
        itemTitle=""
        onConfirm={handleConfirmCancel}
        onCancel={() => setShowCancelModal(false)}
        isLoading={cancelingId !== null}
        confirmLabel="Cancel Booking"
        cancelLabel="Close"
        icon={faTimes}
        confirmVariant="phoenix-danger"
      />

    </Container>
  );
};

export default BookingReportPage;